import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert } from '../components/Alert';
import { mountAngularLeaf, unmountAngularLeaf } from 'app/angular-leaf.bootstrap';
import { getLeafDisplayedPath } from 'app/react-bridge/leaf-displayed-path';
import { reportError } from '../observability';

/**
 * Key que o React Router atribui a toda location cujo history state não traz key —
 * ou seja, a que veio do browser e não de um `push`/`replace` do próprio router.
 */
const BROWSER_LOCATION_KEY = 'default';

/**
 * Hospeda a folha Angular e controla seu ciclo de remonte por navegação do shell.
 *
 * A folha roda um Angular Router próprio e navega entre suas rotas via pushState,
 * sem emitir hashchange/popstate — então o React Router do shell não fica sabendo
 * dessas navegações internas e seu pathname fica defasado. Por isso a decisão de
 * remontar NÃO pode se basear só no pathname do React Router: quando o usuário
 * volta da rota filha para a pai pelo menu, o pathname alvo é igual ao (defasado)
 * pathname atual, e um remount amarrado a ele não dispararia — a URL mudaria mas a
 * tela continuaria na filha.
 *
 * A cada navegação client-side do shell comparamos o pathname alvo com a rota que a
 * folha realmente exibe (getLeafDisplayedPath, alimentado pelos NavigationEnd da
 * folha). Só remontamos quando divergem: uma navegação que muda só a query (mesmo
 * pathname, folha na mesma rota) não remonta — preservando estado como uma chamada
 * WebRTC do softphone — mas voltar da filha para a pai remonta e reroteia a folha ao
 * hash corrente.
 *
 * Navegação vinda do browser é o caso oposto e não deve remontar: ver a nota sobre
 * `BROWSER_LOCATION_KEY` no efeito.
 */
export function AngularLeafHost() {
  const { pathname, key: locationKey } = useLocation();
  const [mountId, setMountId] = useState(0);
  const lastHandledKey = useRef<string | null>(null);

  useEffect(() => {
    // Replay de efeito do StrictMode (mesma key): já tratada, não reprocessa.
    if (lastHandledKey.current === locationKey) {
      return;
    }
    const isInitialMount = lastHandledKey.current === null;
    lastHandledKey.current = locationKey;
    // Na montagem inicial a folha já sobe roteada para o hash corrente: nada a
    // re-sincronizar. Só navegações subsequentes do shell decidem remount.
    if (isInitialMount) {
      return;
    }

    // Navegação originada no browser (a escrita crua de `location.hash` que cruza a
    // fronteira Angular→React, ou back/forward): o browser emite `popstate`, o Router
    // da folha o recebe e já está reroteando por conta própria. Remontar aqui destrói
    // essa navegação em voo, e o Angular responde ao cancelamento com `restoreHistory`
    // — que reescreve `window.location` para a rota anterior da folha via
    // `replaceState`, sem emitir evento. A travessia se desfaz em silêncio e o usuário
    // precisa repetir a ação.
    if (locationKey === BROWSER_LOCATION_KEY) {
      return;
    }

    // A folha exibe outra rota que não o pathname alvo do shell: força novo ciclo de
    // montagem para que ela re-bootstrape e roteie ao hash corrente.
    if (getLeafDisplayedPath() !== pathname) {
      setMountId((id) => id + 1);
    }
  }, [locationKey, pathname]);

  return <LeafMountPoint key={mountId} />;
}

/**
 * Ponto de montagem da folha: a `key` (mountId) controlada pelo AngularLeafHost dá a
 * este nó uma identidade nova quando é preciso re-sincronizar, fazendo o React
 * desmontar (cleanup → unmountAngularLeaf) e montar (mountAngularLeaf bootstrapa e
 * roteia a folha ao hash corrente), reproduzindo o ciclo do full reload.
 */
function LeafMountPoint(): JSX.Element {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [mountFailed, setMountFailed] = useState(false);

  useEffect(() => {
    if (ref.current) {
      // A rejeição não é um throw de render, então um ErrorBoundary não a captura;
      // por isso o estado de erro é tratado aqui, no ponto que chama o bootstrap.
      mountAngularLeaf(ref.current).catch((error) => {
        reportError(error, { origin: 'angular_leaf_mount' });
        setMountFailed(true);
      });
    }
    return () => {
      void unmountAngularLeaf();
    };
  }, []);

  return (
    <div ref={ref} className="angular-leaf-host">
      {mountFailed && <Alert severity="error">{t('leaf.mountError')}</Alert>}
    </div>
  );
}
